import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LogoMark, WordMark } from "@/components/Logo";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-forest-900/80 backdrop-blur-xl border-b border-bone-100/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-5 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-logo"
          className="inline-flex items-center gap-3 text-bone-100"
        >
          <LogoMark size={36} />
          <WordMark className="text-2xl md:text-[1.75rem]" />
        </Link>

        <nav className="hidden md:flex items-center gap-10 text-sm text-bone-100/80">
          <button
            data-testid="nav-products"
            onClick={() => scrollTo("products")}
            className="link-underline"
          >
            Coverage
          </button>
          <button
            data-testid="nav-process"
            onClick={() => scrollTo("process")}
            className="link-underline"
          >
            Process
          </button>
          <button
            data-testid="nav-faq"
            onClick={() => scrollTo("faq")}
            className="link-underline"
          >
            FAQ
          </button>
          <button
            data-testid="nav-contact"
            onClick={() => scrollTo("contact-us")}
            className="link-underline"
          >
            Contact
          </button>
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="header-get-quote-btn"
            onClick={() => navigate("/quote")}
            className="hidden md:inline-flex items-center gap-2 bg-terracotta-500 text-forest-900 hover:bg-terracotta-400 transition-all px-5 py-2.5 text-sm tracking-wide font-medium"
          >
            Get a Quote
            <span aria-hidden>→</span>
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-bone-100"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-forest-900 border-t border-bone-100/10 px-6 py-6 flex flex-col gap-4">
          <button data-testid="mobile-nav-products" onClick={() => scrollTo("products")} className="text-left text-bone-100">Coverage</button>
          <button data-testid="mobile-nav-process" onClick={() => scrollTo("process")} className="text-left text-bone-100">Process</button>
          <button data-testid="mobile-nav-faq" onClick={() => scrollTo("faq")} className="text-left text-bone-100">FAQ</button>
          <button data-testid="mobile-nav-contact" onClick={() => scrollTo("contact-us")} className="text-left text-bone-100">Contact</button>
          <button
            data-testid="mobile-get-quote-btn"
            onClick={() => { setOpen(false); navigate("/quote"); }}
            className="bg-terracotta-500 text-forest-900 py-3 text-sm mt-2 font-medium"
          >
            Get a Quote →
          </button>
        </div>
      )}
    </header>
  );
};

export default Header;
